import { useState, useEffect, useCallback, useRef } from 'react';
import type { CalendarEvent } from '@/types';
import * as db from '@/lib/db';
import { pullEvents, pushEvents, mergeEvents, getLastSyncTime, setLastSyncTime } from '@/lib/sync';

const SYNC_INTERVAL = 30_000; // 30 seconds

export type SyncStatus = 'idle' | 'syncing' | 'error' | 'offline';

export function useEvents() {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const eventsRef = useRef<CalendarEvent[]>([]);

  // Keep ref in sync
  useEffect(() => { eventsRef.current = events; }, [events]);

  const applyEvents = useCallback((list: CalendarEvent[]) => {
    setEvents(list);
    eventsRef.current = list;
  }, []);

  // Load from IndexedDB
  const loadLocal = useCallback(async (): Promise<CalendarEvent[]> => {
    const all = await db.getAllEvents();
    applyEvents(all);
    return all;
  }, [applyEvents]);

  // Full sync cycle
  const sync = useCallback(async (localOverride?: CalendarEvent[]) => {
    setSyncStatus('syncing');
    try {
      const local = localOverride ?? eventsRef.current;
      const remote = await pullEvents();

      if (remote === null) {
        // Network unavailable
        setSyncStatus('offline');
        return;
      }

      const lastSyncTime = getLastSyncTime();
      const merged = mergeEvents(local, remote, lastSyncTime);
      const now = Date.now();

      // Persist merged state to IndexedDB
      const existingIds = new Set(local.map(e => e.id));
      const mergedIds = new Set(merged.map(e => e.id));

      for (const e of merged) {
        if (!existingIds.has(e.id) || e.updatedAt !== local.find(l => l.id === e.id)?.updatedAt) {
          await db.addEvent(e); // idb put is upsert
        }
      }
      for (const e of local) {
        if (!mergedIds.has(e.id)) await db.deleteEvent(e.id);
      }

      applyEvents(merged);

      // Push merged back to server so everyone stays consistent
      await pushEvents(merged);
      setLastSyncTime(now);
      setLastSync(new Date(now));
      setSyncStatus('idle');
    } catch (err) {
      console.error('[sync]', err);
      setSyncStatus('error');
    }
  }, [applyEvents]);

  // Init: load local, then sync
  useEffect(() => {
    let cancelled = false;
    loadLocal().then(local => {
      if (!cancelled) sync(local);
    }).finally(() => setLoading(false));

    const interval = setInterval(() => {
      if (!cancelled) sync();
    }, SYNC_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [loadLocal, sync]);

  // ── CRUD ──

  const addEvent = useCallback(async (event: CalendarEvent) => {
    await db.addEvent(event);
    const next = [...eventsRef.current, event];
    applyEvents(next);
    pushEvents(next); // fire-and-forget
  }, [applyEvents]);

  const updateEvent = useCallback(async (event: CalendarEvent) => {
    const updated = { ...event, updatedAt: Date.now() };
    await db.updateEvent(updated);
    const next = eventsRef.current.map(e => e.id === event.id ? updated : e);
    applyEvents(next);
    pushEvents(next);
  }, [applyEvents]);

  const deleteEvent = useCallback(async (id: string) => {
    await db.deleteEvent(id);
    const next = eventsRef.current.filter(e => e.id !== id);
    applyEvents(next);
    pushEvents(next);
  }, [applyEvents]);

  const getEventsForDate = useCallback(
    (dateStr: string) => events.filter(e => e.date === dateStr),
    [events]
  );

  return {
    events,
    loading,
    syncStatus,
    lastSync,
    addEvent,
    updateEvent,
    deleteEvent,
    getEventsForDate,
    reload: loadLocal,
    sync,
  };
}
